import {
  memo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from "react";
import { Card, Flex, Skeleton, Typography } from "antd";
import { AudioOutlined } from "@ant-design/icons";
import { motion, AnimatePresence } from "motion/react";
import type { SectionWithSlides } from "hooks/useStoryboardWithSlides";
import { StreamingText } from "./StreamingText";
import { ANIMATION } from "./animationConfig";
import { ThinkingProcessDisplay } from "./ThinkingProcessDisplay";
import { LoadingAnimation } from "./LoadingAnimation";
import { parseScriptBySlides, StreamingSlidePreview } from "./";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import type {
  Templates,
  TemplateState,
} from "pages/media/components/storyboardEditor/types";
import { styled } from "styled-components";
import { usePreviewResize } from "pages/media/components/storyboardEditor/hooks/usePreviewResize/";
import { useBrandKit } from "hooks/useBrandKit/";
import { useAntTheme } from "hooks/useAntTheme/";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { radius } from "@libertify/ui";

const VoiceoverNarration = memo(
  ({
    script,
    isActive,
    onComplete,
  }: {
    script: string;
    isActive: boolean;
    onComplete?: () => void;
  }) => {
    const hasCalledComplete = useRef(false);

    // If there's no script but it's active, notify completion (only once)
    useEffect(() => {
      if (isActive && !script && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [isActive, script, onComplete]);

    if (!isActive || !script) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <Flex
          gap={16}
          align="flex-start"
          style={{
            padding: 30,
            borderRadius: radius.box,
            backgroundColor: "#F2F5F9",
          }}
        >
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: radius.circle,
              backgroundColor: "#3BD4CA",
            }}
          >
            <AudioOutlined style={{ fontSize: 20, color: "#fff" }} />
          </Flex>
          <Flex vertical gap={8} style={{ flex: 1 }}>
            <Typography.Text
              style={{
                color: "#585858",
                fontFamily: "Poppins, sans-serif",
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Voiceover Narration
            </Typography.Text>
            <Typography.Text
              style={{
                margin: 0,
                color: "#000",
                fontFamily: "Poppins, sans-serif",
                fontSize: 16,
                fontStyle: "italic",
                fontWeight: 600,
                lineHeight: "24px",
                whiteSpace: "pre-wrap",
              }}
            >
              &ldquo;
              <StreamingText
                text={script.trim()}
                animate
                wordDelay={ANIMATION.WORD_DELAY}
                onComplete={onComplete}
              />
              &rdquo;
            </Typography.Text>
          </Flex>
        </Flex>
      </motion.div>
    );
  }
);

VoiceoverNarration.displayName = "VoiceoverNarration";

interface MainPanelProps {
  sections: SectionWithSlides[];
  isLoading: boolean;
  activeSlideId: string | null;
  setActiveSlideId: (slideId: string) => void;
  activeSlideSourceRef: MutableRefObject<"user" | "animation">;
  thinkingProcess: string[];
  templates: Templates;
  mediaAspectRatio: number;
  brandKitData: ReturnType<typeof useBrandKit>;
  skipAnimation?: boolean;
}

// Slide component: shows template directly + voiceover animation
const SlideWithVoiceover = memo(
  ({
    slide,
    voiceoverScript,
    isActive,
    onComplete,
    activeSlideId,
    template,
    previewSize,
    brandKitData,
    skipAnimation,
  }: {
    slide: SectionWithSlides["slides"][0];
    voiceoverScript: string;
    isActive: boolean;
    onComplete?: () => void;
    activeSlideId?: string | null;
    template?: TemplateState;
    previewSize?: { width: number; height: number };
    brandKitData: ReturnType<typeof useBrandKit>;
    skipAnimation: boolean;
  }) => {
    const { colorPrimary } = useAntTheme();
    const [voiceoverDone, setVoiceoverDone] = useState(skipAnimation);
    const hasCalledComplete = useRef(false);

    useEffect(() => {
      if (voiceoverDone && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [voiceoverDone, onComplete]);

    const handleVoiceoverComplete = () => {
      setTimeout(() => {
        setVoiceoverDone(true);
      }, 800);
    };

    if (!isActive) return <SlidesSkeleton />;

    const hasTemplate = !!template && !!previewSize;

    return (
      <motion.div
        initial={skipAnimation ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          skipAnimation
            ? { duration: 0 }
            : { duration: 0.4, ease: "easeOut", delay: 0.3 }
        }
      >
        <ScenesFlexbox data-slide-id={slide.uuid}>
          {hasTemplate ? (
            <motion.div
              initial={skipAnimation ? false : { opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={
                skipAnimation
                  ? { duration: 0 }
                  : { duration: 0.5, ease: "easeOut" }
              }
            >
              <StreamingSlidePreview
                template={template}
                templateSize={previewSize}
                slide={slide}
                brandKitData={brandKitData}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onCardClick={() => {}}
                border={{
                  borderWidth: activeSlideId === slide.uuid ? 4 : 0,
                  borderRadius: radius.box,
                  borderColor:
                    activeSlideId === slide.uuid ? colorPrimary : undefined,
                }}
              />
            </motion.div>
          ) : (
            <SlidesSkeleton />
          )}
          <VoiceoverNarration
            script={voiceoverScript}
            isActive={hasTemplate}
            onComplete={handleVoiceoverComplete}
          />
        </ScenesFlexbox>
      </motion.div>
    );
  }
);

SlideWithVoiceover.displayName = "SlideWithVoiceover";

const SlidesSkeleton = memo(() => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: 0.2 }}
  >
    <Card>
      <Skeleton active paragraph={{ rows: 3 }} />
    </Card>
  </motion.div>
));

SlidesSkeleton.displayName = "SlidesSkeleton";

// Section component — renders slides sequentially
const SectionItem = memo(
  ({
    section,
    activeSlideId,
    setActiveSlideId,
    isActive,
    onSectionComplete,
    templates,
    previewSize,
    brandKitData,
    skipAnimation,
  }: {
    section: SectionWithSlides;
    activeSlideId?: string | null;
    setActiveSlideId: (slideId: string) => void;
    isActive: boolean;
    onSectionComplete?: () => void;
    templates: Templates;
    previewSize: { width: number; height: number };
    brandKitData: ReturnType<typeof useBrandKit>;
    skipAnimation: boolean;
  }) => {
    const hasSlides = section.slides.length > 0;
    const voiceoverScripts = parseScriptBySlides(section.script.content);

    const [internalSlideIndex, setInternalSlideIndex] = useState(
      skipAnimation ? Infinity : 0
    );
    const [sectionAnimationDone, setSectionAnimationDone] =
      useState(skipAnimation);

    const currentSlideIndex =
      !isActive || !hasSlides
        ? -1
        : sectionAnimationDone
          ? Infinity
          : internalSlideIndex;

    // Set the active slide ID when the current slide index advances
    useEffect(() => {
      if (currentSlideIndex >= 0 && currentSlideIndex < section.slides.length) {
        setActiveSlideId(section.slides[currentSlideIndex].uuid);
      }
    }, [currentSlideIndex, section.slides, setActiveSlideId]);

    const handleSlideComplete = (slideIndex: number) => {
      if (slideIndex < section.slides.length - 1) {
        setInternalSlideIndex(slideIndex + 1);
      } else {
        setSectionAnimationDone(true);
        onSectionComplete?.();
      }
    };

    const showSkeleton = !hasSlides || currentSlideIndex === -1;

    return (
      <Flex vertical gap={24}>
        {showSkeleton ? (
          <SlidesSkeleton />
        ) : (
          section.slides.map((slide, slideIndex) => {
            const template = templates.get(slide.uuid);
            return (
              <SlideWithVoiceover
                key={slide.uuid}
                slide={slide}
                voiceoverScript={
                  parseScript(voiceoverScripts.get(slide.uuid) ?? "").text
                }
                isActive={slideIndex <= currentSlideIndex}
                onComplete={() => handleSlideComplete(slideIndex)}
                activeSlideId={activeSlideId}
                template={template}
                previewSize={previewSize}
                brandKitData={brandKitData}
                skipAnimation={skipAnimation}
              />
            );
          })
        )}
      </Flex>
    );
  }
);

SectionItem.displayName = "SectionItem";

export const MainPanel = memo(
  ({
    sections,
    isLoading: _isLoading,
    activeSlideId,
    setActiveSlideId,
    activeSlideSourceRef,
    thinkingProcess,
    templates,
    mediaAspectRatio,
    brandKitData,
    skipAnimation = false,
  }: MainPanelProps) => {
    const {
      previewSizes: { scene: previewSize },
    } = usePreviewResize(mediaAspectRatio);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { scrollToElement } = useAutoScroll(scrollContainerRef);

    // Scroll position preservation: save scrollTop continuously,
    // restore it synchronously (before paint) if a data change causes a jump.
    const savedScrollTopRef = useRef(0);

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        savedScrollTopRef.current = container.scrollTop;
      };

      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    // After sections/templates change, check if scroll was reset unexpectedly
    useLayoutEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const saved = savedScrollTopRef.current;
      // If we had meaningful scroll position and it suddenly jumped to top,
      // restore it synchronously before the browser paints
      if (saved > 50 && container.scrollTop === 0) {
        container.scrollTop = saved;
      }
    }, [sections, templates]);

    // Centralized scroll: scroll to the active slide whenever it changes
    useEffect(() => {
      if (!activeSlideId) return;

      const force = activeSlideSourceRef.current === "user";

      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const slideElement = container.querySelector<HTMLElement>(
          `[data-slide-id="${activeSlideId}"]`
        );
        if (slideElement) {
          scrollToElement(
            slideElement,
            { behavior: "smooth", block: "end" },
            { force }
          );
          if (force) {
            activeSlideSourceRef.current = "animation";
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [activeSlideId, scrollToElement, activeSlideSourceRef]);

    // Thinking process is independent — it displays and animates on its own
    // without blocking section rendering
    const hasThinkingProcess = thinkingProcess.length > 0;

    // Per-section sequential animation
    const [currentSectionIndex, setCurrentSectionIndex] = useState(
      skipAnimation ? Infinity : 0
    );
    const [allSectionsAnimated, setAllSectionsAnimated] =
      useState(skipAnimation);

    const handleSectionComplete = (sectionIndex: number) => {
      if (sectionIndex < sections.length - 1) {
        setCurrentSectionIndex(sectionIndex + 1);
      } else {
        setAllSectionsAnimated(true);
      }
    };

    // One-way latch: once we've shown slides, never go back to loading
    const [hasShownSlides, setHasShownSlides] = useState(skipAnimation);
    if (templates.size > 0 && !hasShownSlides) {
      setHasShownSlides(true);
    }
    const showLoadingAnimation = !hasShownSlides;

    return (
      <StyledScrollableFlex
        ref={scrollContainerRef}
        vertical
        style={{
          padding: showLoadingAnimation ? "24px 24px 0" : 24,
          gap: showLoadingAnimation ? 0 : 24,
          transition: "padding 0.5s ease, gap 0.5s ease",
        }}
      >
        {/* ThinkingProcess — stable tree position, never remounted */}
        {hasThinkingProcess && (
          <ThinkingProcessDisplay texts={thinkingProcess} isActive={true} />
        )}

        {/* Animated transition between loading and content */}
        <AnimatePresence mode="wait">
          {showLoadingAnimation ? (
            <motion.div
              key="loading-animation"
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
              }}
            >
              <LoadingAnimation isVisible />
            </motion.div>
          ) : (
            <motion.div
              key="slides-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            >
              <Flex vertical gap={24}>
                {sections.map((section, sectionIndex) => (
                  <SectionItem
                    key={section.id}
                    section={section}
                    activeSlideId={activeSlideId}
                    setActiveSlideId={setActiveSlideId}
                    isActive={
                      allSectionsAnimated || sectionIndex <= currentSectionIndex
                    }
                    onSectionComplete={() =>
                      handleSectionComplete(sectionIndex)
                    }
                    templates={templates}
                    previewSize={previewSize}
                    brandKitData={brandKitData}
                    skipAnimation={skipAnimation}
                  />
                ))}
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledScrollableFlex>
    );
  }
);

MainPanel.displayName = "MainPanel";

const ScenesFlexbox = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap ?? 0}px;
  padding-inline: 24px 40px;
`;

const StyledScrollableFlex = styled(Flex)`
  width: 100%;
  overflow-y: scroll;
  overflow-anchor: auto;
  position: relative;
  flex: 1;
  transition: padding-inline-end 0.3s ease-in-out;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;

  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
    opacity: 0.5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colorTextDescription};
    opacity: 0.7;
  }
`;
